"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 91: J.Y. Company (Street Fighter 3, Mortal Kombat II bootlegs)
 * ------------------------------------------------------------------ *
 * Two boards share this number and the iNES header cannot say which one a dump
 * came from, so submapper 0 -- the JY830623C / YY840238C, the one every bootleg
 * in the wild uses -- is what loads by default:
 *
 *   CPU $8000  8 KB switchable        PPU $0000  2 KB switchable
 *       $A000  8 KB switchable            $0800  2 KB switchable
 *       $C000  16 KB fixed to the last    $1000  2 KB switchable
 *       $E000  ...                        $1800  2 KB switchable
 *
 * Register map follows https://www.nesdev.org/wiki/INES_Mapper_091:
 *
 *   $6000-$6003  CHR 2 KB bank select
 *   $6004-$6005  nametable mirroring         (submapper 1 only)
 *   $6006-$6007  IRQ counter LSB / MSB       (submapper 1 only)
 *   $7000-$7001  PRG 8 KB bank select
 *   $7006        IRQ stop / acknowledge
 *   $7007        IRQ start / reset
 *   $8000-$9FFF  outer bank select           (submapper 0 only)
 *
 * The decode masks ($F003 for submapper 0, $F007 for submapper 1) are what make
 * $7006 land on register 2 and $7007 on register 3: bits 0-1 are the register
 * number for submapper 0, so the whole $6000-$7FFF span aliases down onto four
 * registers, while submapper 1 keeps bits 0-2 and sees eight.
 *
 * The two submappers count differently:
 *   - Submapper 0 counts 64 unfiltered PPU A12 rises, which is eight scanlines
 *     at the normal pattern-table layout, so the split lands at a fixed point.
 *   - Submapper 1 counts down five every fourth CPU cycle.
 * They also differ on mirroring: submapper 0's is hard-wired on the board, so
 * the iNES header is the authority and nothing here touches it, while submapper
 * 1 selects it with a write.
 *
 * Submapper 1 is not what any common dump declares, so it is only reachable by
 * setting `nes.mmap.submapper = 1` before the mapper loads. Everything defaults
 * to the arrangement the games actually run on.
 * ------------------------------------------------------------------ */

// The same RC filter the MMC3/MMC2/MMC4 counters sit behind: a rise only counts
// once A12 has been low for eight dots, which throws away the toggles a CPU
// access to $0000-$1FFF through $2006/$2007 can produce.
var A12_FILTER_DOTS = 8;

function register(Mappers) {
  Mappers[91] = function (nes) {
    this.nes = nes;
  };

  Mappers[91].prototype = new Mappers[0]();

  // 0 = JY830623C / YY840238C, 1 = EJ-006-1.
  Mappers[91].prototype.submapper = 0;

  // 64 unfiltered A12 rises. The PPU reports the filtered rate -- one rise per
  // rendered scanline -- so the counter has to see eight notifications, not 64.
  Mappers[91].prototype.A12_EVENTS_PER_IRQ = 8;

  Mappers[91].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    this.prgReg = [0, 1];
    this.chrReg = [0, 0, 0, 0];

    this.irqEnabled = false;
    this.irqPending = false;
    // Submapper 0: A12 rises counted towards the next IRQ.
    this.irqCount = 0;
    // Submapper 1: 16-bit down counter and the value it reloads from.
    this.irqCounter = 0;
    this.irqLatch = 0;
    this.m2Phase = 0;

    // $8000-$9FFF, submapper 0 only.
    this.outerBank = 0;

    this.syncClockSource();
  };

  // Which hook NES.frame()/the PPU drive. Set here rather than on the prototype
  // because it depends on the submapper chosen for this instance.
  Mappers[91].prototype.syncClockSource = function () {
    if (this.submapper === 1) {
      this.watchesA12 = false;
      this.watchesCpuCycles = true;
      this.irqClockRate = "scanline";
    } else {
      this.watchesA12 = true;
      this.watchesCpuCycles = false;
      this.irqClockRate = "a12";
    }
  };

  Mappers[91].prototype.write = function (address, value) {
    if (address < 0x6000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // $8000-$9FFF on submapper 0 is the outer bank register; every other address
    // from $8000 up is ROM and swallows the write.
    if (address >= 0x8000) {
      if (this.submapper === 0 && address <= 0x9fff) {
        this.outerBank = value;
        this.updatePrgBanks();
        this.updateChrBanks();
      }
      return;
    }

    // $6000-$7FFF: registers. Mask $F003 for submapper 0, $F007 for submapper 1.
    var reg = address & (this.submapper === 1 ? 0x07 : 0x03);

    if (address < 0x7000) {
      if (reg < 4) {
        this.chrReg[reg] = value;
        this.updateChrBanks();
        return;
      }
      if (this.submapper === 1) {
        switch (reg) {
          case 4:
            this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
            return;
          case 5:
            this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
            return;
          case 6:
            this.irqLatch = (this.irqLatch & 0xff00) | (value & 0xff);
            return;
          case 7:
            this.irqLatch = (this.irqLatch & 0x00ff) | ((value & 0xff) << 8);
            return;
        }
      }
      return;
    }

    // $7000-$7FFF. The stop/start registers move with the mask: bits 0-1 give
    // $7006/$7007 on submapper 0 and 1 as well, but submapper 1 keeps bits 0-2,
    // where they are $7006/$7007 out of eight rather than two out of four.
    var stopReg = this.submapper === 1 ? 6 : 2;
    var startReg = this.submapper === 1 ? 7 : 3;

    switch (reg) {
      case 0:
        this.prgReg[0] = value;
        this.updatePrgBanks();
        return;
      case 1:
        this.prgReg[1] = value;
        this.updatePrgBanks();
        return;
      default:
        if (reg === stopReg) {
          this.stopIrq();
        } else if (reg === startReg) {
          this.startIrq();
        }
        return;
    }
  };

  // ---- banking ----

  Mappers[91].prototype.updatePrgBanks = function () {
    var rom = this.nes.rom;
    var banks8k = rom.romCount * 2;
    if (banks8k === 0) {
      return;
    }

    // The outer register's bits 1-2 are PRG A17-A18. An 8 KB bank is numbered
    // from A13, so A17 is 8 KB bank bit 4 and A18 bank bit 5: one outer step is
    // 128 KB, i.e. sixteen 8 KB banks.
    var outer = (this.outerBank & 0x06) << 3;

    this.load8kRomBank((this.prgReg[0] + outer) % banks8k, 0x8000);
    this.load8kRomBank((this.prgReg[1] + outer) % banks8k, 0xa000);

    // $C000-$FFFF is the last 16 KB of the cartridge. It is not moved by the
    // outer bank: it is the block a multicart's menu lives in.
    this.load8kRomBank(banks8k - 2, 0xc000);
    this.load8kRomBank(banks8k - 1, 0xe000);
  };

  Mappers[91].prototype.updateChrBanks = function () {
    var rom = this.nes.rom;
    if (rom.vromCount === 0) {
      return;
    }

    // Bit 0 is CHR A19. A 2 KB bank is numbered from A11, so A19 is bank bit 8:
    // one outer step is 256 x 2 KB, i.e. the 512 KB the register description
    // calls for.
    var banks2k = rom.vromCount * 2;
    var outer = (this.outerBank & 0x01) << 8;

    for (var i = 0; i < 4; i++) {
      this.load2kVromBank((this.chrReg[i] + outer) % banks2k, i * 0x800);
    }
  };

  // ---- IRQ ----

  // Submapper 0: eight filtered A12 rises, which stand for the 64 unfiltered
  // ones the board counts.
  Mappers[91].prototype.notifyA12 = function (level, lowDots) {
    if (this.submapper !== 0) {
      return;
    }
    if (level !== 1 || lowDots < A12_FILTER_DOTS) {
      return;
    }
    if (!this.irqEnabled) {
      return;
    }

    // Once the window is full it stays full until $7006 stops it, which is what
    // keeps the counter from running off during the lines that follow.
    if (this.irqCount < this.A12_EVENTS_PER_IRQ) {
      this.irqCount++;
      if (this.irqCount >= this.A12_EVENTS_PER_IRQ) {
        this.raiseIrq();
      }
    }
  };

  // Submapper 1: five decrements every fourth CPU cycle.
  Mappers[91].prototype.clockCpuCycles = function (cycles) {
    if (this.submapper !== 1 || !this.irqEnabled) {
      return;
    }

    this.m2Phase += cycles;
    var ticks = Math.floor(this.m2Phase / 4);
    if (ticks === 0) {
      return;
    }
    this.m2Phase -= ticks * 4;

    var value = this.irqCounter - ticks * 5;
    if (value < 0) {
      // Underflow to $FFFF, and the line goes low on the way.
      this.irqCounter = value + 0x10000;
      this.raiseIrq();
    } else {
      this.irqCounter = value;
    }
  };

  // Disabling acknowledges: the register description gives $7006 no payload and
  // calls it "stop / acknowledge", so it clears the pending flag and the window.
  Mappers[91].prototype.stopIrq = function () {
    this.irqEnabled = false;
    this.irqPending = false;
    this.irqCount = 0;
    this.irqCounter = 0;
    this.m2Phase = 0;
  };

  // "Start / reset". Re-arming a counter that has already fired, or was never
  // running, opens a fresh window; re-writing $7007 while the window is still
  // open must not restart it, or a game that pokes the register more than once
  // per frame would never reach the end of one.
  Mappers[91].prototype.startIrq = function () {
    if (!this.irqEnabled || this.irqCount >= this.A12_EVENTS_PER_IRQ) {
      this.irqCount = 0;
    }
    if (this.submapper === 1) {
      this.irqCounter = this.irqLatch;
    }
    this.irqEnabled = true;
    this.irqPending = false;
  };

  Mappers[91].prototype.raiseIrq = function () {
    if (this.irqPending) {
      return;
    }
    this.irqPending = true;
    this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
  };

  // ---- power on ----

  Mappers[91].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("J.Y. Company mapper 91: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // The power-on values the bootlegs depend on.
    this.prgReg[0] = 0;
    this.prgReg[1] = 1;

    this.updatePrgBanks();
    this.updateChrBanks();

    // Mirroring is left as the header asked for: submapper 0 hard-wires it on
    // the board, and the header is the only thing that can describe it.
    this.resetIrqHandler();
  };

  Mappers[91].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgReg = this.prgReg.slice();
    s.chrReg = this.chrReg.slice();
    s.irqEnabled = this.irqEnabled;
    s.irqPending = this.irqPending;
    s.irqCount = this.irqCount;
    s.irqCounter = this.irqCounter;
    s.irqLatch = this.irqLatch;
    s.m2Phase = this.m2Phase;
    s.outerBank = this.outerBank;
    return s;
  };

  Mappers[91].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgReg = s.prgReg.slice();
    this.chrReg = s.chrReg.slice();
    this.irqEnabled = s.irqEnabled;
    this.irqPending = s.irqPending;
    this.irqCount = s.irqCount;
    this.irqCounter = s.irqCounter;
    this.irqLatch = s.irqLatch;
    this.m2Phase = s.m2Phase;
    this.outerBank = s.outerBank;
    this.syncClockSource();
    // The CPU and PPU hold their own copies of the banks, so re-derive them.
    this.updatePrgBanks();
    this.updateChrBanks();
  };
}

module.exports = { register: register };
